import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  startTransition,
} from 'react'
import {
  Button,
  Tab,
  Tabs,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Box,
  IconButton,
  Tooltip,
  styled,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import CloseIcon from '@mui/icons-material/Close'
import ScaleLoader from 'react-spinners/ScaleLoader'
import { makeStyles } from '../../utils/hooks/makeStyles'
import CreateCampComponent from './component/CreateCamp'
import ReportPreviewDialog from './component/ReportPreviewDialog'
import campReportCellRenderer from './component/CampReportCellRenderer'
import {
  createCampType,
  getCampReportById,
  getCampsList,
  getCompletedCampsList,
  getUpcomingCampsList,
  bulkUpload,
  downloadCampLabOrders,
} from '../../services/CampManagementService'
import { toast } from 'react-toastify'
import ToastMessage from '../lms/components/ToastMessage'
import { toastOptions } from '../lms/models'
import { useNavigate } from 'react-router-dom'
import { useAppDispatch } from '../../app/hooks'
import { setSelectedCamp } from './campManagementSlice'
import DateFilter from './component/DateFilter'
import styles from './listOfCamps.module.scss'
import {
  campTabs,
  textFailed,
  textSuccess,
  visitDashboardConstants,
} from './constants/Constants'
import MedAgDataGrid from '../../components/MedAgDataGrid'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'

const StyledTabs = styled(Tabs)(() => ({
  border: '2px solid #4682b4',
  borderRadius: '8px',
  backgroundColor: 'transparent',
  minHeight: '37px',
  overflow: 'hidden',
  '& .MuiTabs-flexContainer': {
    height: '37px',
  },
  '& .MuiButtonBase-root': {
    textTransform: 'capitalize',
    minHeight: '37px',
    transition: 'all 0.3s ease',
    borderRight: '1px solid #4682b4',
    '&:last-child': {
      borderRight: 'none',
    },
  },
  '& .MuiTab-root': {
    fontSize: '13px',
    fontWeight: 500,
    color: '#4682b4',
    // padding: '12px 24px',
    backgroundColor: 'transparent',
    '&:hover': {
      backgroundColor: 'rgba(70, 130, 180, 0.08)',
    },
  },
  '& .Mui-selected': {
    fontWeight: 600,
    fontSize: '13px',
    color: '#ffffff !important',
    backgroundColor: '#4682b4 !important',
  },
  '& .MuiTabs-indicator': {
    display: 'none',
  },
}))

const useStyles = makeStyles((theme: any) => ({
  uploadContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    marginTop: theme.spacing(2),
    textAlign: 'center',
    width: '100%',
  },
  fileInfoContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  fileInfo: {
    marginTop: theme.spacing(2),
    textAlign: 'left',
  },
  fileTypeHint: {
    marginTop: theme.spacing(1),
    textAlign: 'center',
    display: 'block',
  },
  dialogActions: {
    padding: theme.spacing(2),
  },
}))

const tabKeys = [campTabs.active, campTabs.upComing, campTabs.completed]

// Helper to format date strings to 'dd-MM-yyyy' for display
const formatDateString = (dateStr?: string) => {
  if (!dateStr) return '-'
  try {
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      const [y, m, d] = dateStr.split('-')
      return `${d}-${m}-${y}`
    }
    return dateStr
  } catch (e) {
    return dateStr
  }
}

const baseColumns = [
  {
    headerName: 'Name',
    field: 'name',
    flex: 1.3,
    minWidth: 200,
    suppressSizeToFit: false,
    cellClass: 'camp-name-cell',
    cellRenderer: (params: any) => (
      <Box
        display='flex'
        flexDirection='row'
        justifyContent='space-between'
        alignItems='center'
        gap={1}
        sx={{ width: '100%', height: '100%' }}
      >
        <Box
          onClick={() => params?.context?.onViewCamp(params?.data)}
          sx={{
            color: '#1976d2',
            cursor: 'pointer',
            textDecoration: 'underline',
            flex: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          <Tooltip title={params?.value || '-'}>{params?.value || '-'}</Tooltip>
        </Box>
        <Box sx={{ flexShrink: 0 }}>
          <Tooltip title='Edit Camp'>
            <IconButton
              size='small'
              onClick={(e) => {
                e.stopPropagation()
                params?.context?.onEditCamp(params?.data)
              }}
            >
              <EditOutlinedIcon fontSize='small' />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    ),
  },
  {
    headerName: 'Type',
    field: 'campType',
    flex: 1,
    minWidth: 120,
    suppressSizeToFit: false,
    valueGetter: (params: any) => {
      const ct = params?.data?.campType
      if (!ct) return '-'
      if (typeof ct === visitDashboardConstants?.defaults?.string)
        return ct || '-'
      return ct?.name || '-'
    },
  },
  {
    headerName: 'Location',
    field: 'location',
    flex: 1,
    minWidth: 120,
    suppressSizeToFit: false,
  },
  {
    headerName: 'From Date',
    field: 'fromDate',
    flex: 0.9,
    minWidth: 110,
    suppressSizeToFit: false,
    valueGetter: (params: any) =>
      formatDateString(params?.data?.fromDate ?? params?.data?.date),
  },
  {
    headerName: 'To Date',
    field: 'toDate',
    flex: 0.9,
    minWidth: 110,
    suppressSizeToFit: false,
    valueGetter: (params: any) =>
      formatDateString(params?.data?.toDate ?? params?.data?.date),
  },
  {
    headerName: 'Registrations',
    field: 'numberOfRegistrations',
    flex: 1,
    minWidth: 130,
    suppressSizeToFit: false,
  },
  {
    headerName: 'Visits',
    field: 'numberOfVisits',
    flex: 1,
    minWidth: 100,
    suppressSizeToFit: false,
  },
]

const reportColumn = {
  headerName: 'Report',
  field: 'campId',
  flex: 1,
  minWidth: 80,
  suppressSizeToFit: false,
  cellRenderer: campReportCellRenderer,
}

const ListOfCamps: React.FC = () => {
  const [tab, setTab] = useState(0)
  const [open, setOpen] = useState(false)
  const [fromDate, setFromDate] = useState('')
  const [toDate, setToDate] = useState('')
  const [reportDialogOpen, setReportDialogOpen] = useState(false)
  const [reportData, setReportData] = useState<any>(null)
  const [addTypeOpen, setAddTypeOpen] = useState(false)
  const [newType, setNewType] = useState('')
  const [newDesc, setNewDesc] = useState('')
  const [activeCamps, setActiveCamps] = useState<any[]>([])
  const [upcomingCamps, setUpcomingCamps] = useState<any[]>([])
  const [closedCamps, setClosedCamps] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const [loadingType, setLoadingType] = useState(false)

  // pagination state
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(25)
  const [totalPages, setTotalPages] = useState<number | null>(null)
  const [totalElements, setTotalElements] = useState<number | null>(null)
  const [editingCamp, setEditingCamp] = useState<any | null>(null)
  const [openUpdateDialog, setOpenUpdateDialog] = useState(false)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [labReportDialogOpen, setLabReportDialogOpen] = useState(false)
  const [labReportFromDate, setLabReportFromDate] = useState('')
  const [labReportToDate, setLabReportToDate] = useState('')
  const [labReportCamp, setLabReportCamp] = useState<any | null>(null)
  const [isDownloadingLabReport, setIsDownloadingLabReport] = useState(false)
  const [isLoadingReport, setIsLoadingReport] = useState(false)

  const handleFileUpload = async () => {
    if (!selectedFile) return

    try {
      setIsUploading(true)
      const response = await bulkUpload(selectedFile)
      if (response.success) {
        toast.success(response.message || textSuccess)
      } else {
        throw new Error(response.message || 'Upload failed')
      }
      setSelectedFile(null)
      setOpenUpdateDialog(false)
    } catch (error: any) {
      console.error('Upload error:', error)
      toast.error(error.message || textFailed)
    } finally {
      setIsUploading(false)
    }
  }
  const classes = useStyles()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  // helper to extract rows array from API response which may be array or paged object
  const extractRowsFromResp = useCallback((resp: any) => {
    if (!resp) return []
    if (resp.data && Array.isArray(resp.data)) return resp.data
    if (resp.data && Array.isArray(resp.data.content)) return resp.data.content
    if (resp.data && resp.data.data && Array.isArray(resp.data.data))
      return resp.data.data
    if (Array.isArray(resp)) return resp
    return []
  }, [])

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    // Reset to first page when changing tabs
    if (tab !== newValue) {
      setPage(0)
    }
    setTab(newValue)
  }

  const formatDate = useCallback((date: Date) => {
    // Ensure we're working with local time
    const localDate = new Date(date)
    const year = localDate.getFullYear()
    const month = String(localDate.getMonth() + 1).padStart(2, '0')
    const day = String(localDate.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }, [])

  const handleDialogClose = useCallback(() => {
    setOpen(false)
    setEditingCamp(null)
  }, [])

  const safeSetPage = useCallback(
    (newPage: number) => {
      if (totalPages !== null && newPage >= totalPages) return
      if (newPage < 0) return
      setPage(newPage)
    },
    [totalPages]
  )

  const calculateTotalPages = useCallback(() => {
    if (typeof totalElements === 'number' && pageSize > 0) {
      const computed = Math.ceil(totalElements / pageSize)
      setTotalPages((prev) => (prev === null ? computed : prev))
    }
  }, [totalElements, pageSize])

  useEffect(() => {
    calculateTotalPages()
  }, [calculateTotalPages])

  const handleGetActiveCamps = useCallback(
    async (startDate: any, endDate: any, pageNo: number, size: number) => {
      setLoading(true)

      try {
        const resp = await getCampsList(startDate, endDate, pageNo, size)

        if (resp?.success) {
          const rows = extractRowsFromResp(resp)
          setActiveCamps(rows)

          const meta = resp?.data
          if (meta) {
            if (
              typeof meta.totalElements ===
              visitDashboardConstants?.defaults?.number
            ) {
              setTotalElements(meta.totalElements)
              const pages = Math.ceil(meta.totalElements / (size || 1))
              setTotalPages(pages)
            } else if (
              typeof meta.totalPages ===
              visitDashboardConstants?.defaults?.number
            ) {
              setTotalPages(meta.totalPages)
            } else {
              setTotalPages(null)
              setTotalElements(null)
            }
          }
        } else {
          toast.error(`${resp?.message || 'Failed to fetch active camps'}`)
        }
      } catch (err: any) {
        toast.error('Failed to fetch active camps')
      } finally {
        setLoading(false)
      }
    },
    [extractRowsFromResp]
  )

  const handleGetUpcomingCamps = useCallback(
    async (startDate: any, pageNo: number, size: number) => {
      setLoading(true)
      try {
        const resp = await getUpcomingCampsList(startDate, pageNo, size)
        if (resp?.success) {
          const rows = extractRowsFromResp(resp)
          setUpcomingCamps(rows)

          const meta = resp?.data
          if (meta) {
            if (
              typeof meta.totalElements ===
              visitDashboardConstants?.defaults?.number
            ) {
              setTotalElements(meta.totalElements)
              setTotalPages(Math.ceil(meta.totalElements / (size || 1)))
            } else if (
              typeof meta.totalPages ===
              visitDashboardConstants?.defaults?.number
            ) {
              setTotalPages(meta.totalPages)
            } else {
              setTotalPages(null)
              setTotalElements(null)
            }
          }
        } else {
          toast.error(`${resp?.message}`)
        }
      } catch (err: any) {
        toast.error('Failed to fetch upcoming camps')
      } finally {
        setLoading(false)
      }
    },
    [extractRowsFromResp]
  )

  const handleGetCompletedCamps = useCallback(
    async (endDate: any, pageNo: number, size: number) => {
      setLoading(true)
      try {
        const resp = await getCompletedCampsList(endDate, pageNo, size)
        if (resp?.success) {
          const rows = extractRowsFromResp(resp)
          setClosedCamps(rows)

          const meta = resp?.data
          if (meta) {
            if (
              typeof meta.totalElements ===
              visitDashboardConstants?.defaults?.number
            ) {
              setTotalElements(meta.totalElements)
              setTotalPages(Math.ceil(meta.totalElements / (size || 1)))
            } else if (
              typeof meta.totalPages ===
              visitDashboardConstants?.defaults?.number
            ) {
              setTotalPages(meta.totalPages)
            } else {
              setTotalPages(null)
              setTotalElements(null)
            }
          }
        } else {
          toast.error(`${resp?.message}`)
        }
      } catch (err: any) {
        toast.error('Failed to fetch completed camps')
      } finally {
        setLoading(false)
      }
    },
    [extractRowsFromResp]
  )

  const fetchData = useCallback(async () => {
    const now = new Date()
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
    const tomorrow = new Date(today)
    tomorrow.setDate(today.getDate() + 1)
    const yesterday = new Date(today)
    yesterday.setDate(today.getDate() - 1)
    const tabType = tabKeys[tab]

    try {
      setLoading(true)
      if (tabType === campTabs.active) {
        const start = fromDate || formatDate(today)
        const end = toDate || formatDate(today)

        await handleGetActiveCamps(start, end, page, pageSize)
      } else if (tabType === campTabs.upComing) {
        const start = fromDate || formatDate(tomorrow)
        await handleGetUpcomingCamps(start, page, pageSize)
      } else {
        const end = toDate || formatDate(yesterday)
        await handleGetCompletedCamps(end, page, pageSize)
      }
    } catch (error) {
      toast.error('Failed to fetch camps data')
    } finally {
      setLoading(false)
    }
  }, [
    tab,
    page,
    pageSize,
    fromDate,
    toDate,
    formatDate,
    handleGetActiveCamps,
    handleGetUpcomingCamps,
    handleGetCompletedCamps,
  ])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const handlePreviewReport = async (campId: string) => {
    setIsLoadingReport(true)
    setReportDialogOpen(true)
    try {
      const res = await getCampReportById(campId)
      if (res?.success && res?.data && Object.keys(res.data).length > 0) {
        setReportData(res.data)
      } else {
        setReportData(null)
      }
    } catch (err) {
      setReportData(null)
      toast(<ToastMessage message='Failed to fetch report' />, {
        ...toastOptions,
        type: 'error',
      })
    } finally {
      setIsLoadingReport(false)
    }
  }

  const gridData = useMemo(() => {
    const tabType = tabKeys[tab]
    if (tabType === campTabs.active) return activeCamps
    if (tabType === campTabs.upComing) return upcomingCamps
    return closedCamps
  }, [tab, activeCamps, upcomingCamps, closedCamps])

  const columnDefs = useMemo(() => {
    const tabType = tabKeys[tab]

    const sNoColumn = {
      headerName: 'S. No.',
      field: 'sNo',
      flex: 1,
      minWidth: 50,
      suppressSizeToFit: false,
      valueGetter: (params: any) => {
        const indexInPage =
          typeof params?.node?.rowIndex ===
          visitDashboardConstants?.defaults?.number
            ? params?.node?.rowIndex
            : typeof params?.rowIndex ===
              visitDashboardConstants?.defaults?.number
            ? params?.rowIndex
            : 0
        return page * pageSize + indexInPage + 1
      },
    }

    const labColumn = {
      headerName: 'Lab',
      field: 'lab',
      flex: 1,
      minWidth: 100,
      suppressSizeToFit: false,
      cellRenderer: (params: any) =>
        params?.data ? (
          <span
            className={styles.campEditText}
            onClick={() => params?.context?.onOpenLabReport(params?.data)}
          >
            Export
          </span>
        ) : (
          '-'
        ),
    }

    const clinicalColumn = {
      headerName: 'Clinical',
      field: 'clinical',
      flex: 1,
      minWidth: 140,
      suppressSizeToFit: false,
      cellRenderer: (params: any) =>
        params?.data ? (
          <Box display='flex'>
            <span
              className={styles.campEditText}
              onClick={() => params?.context?.onNavigateToECG(params?.data)}
            >
              ECG
            </span>
            <span
              className={styles.campEditText}
              onClick={() => params?.context?.onNavigateToPFT(params?.data)}
            >
              PFT
            </span>
          </Box>
        ) : (
          '-'
        ),
    }

    const viewColumn = {
      headerName: 'Upload',
      field: 'view',
      flex: 1,
      minWidth: 110,
      suppressSizeToFit: false,
      cellRenderer: () => (
        <span
          className={styles.campEditText}
          onClick={() => setOpenUpdateDialog(true)}
        >
          Upload
        </span>
      ),
    }

    if (tabType === campTabs.upComing) {
      return [sNoColumn, ...baseColumns, clinicalColumn, reportColumn]
    } else if (tabType === campTabs.completed) {
      return [sNoColumn, ...baseColumns, labColumn, reportColumn]
    } else {
      return [
        sNoColumn,
        ...baseColumns,
        labColumn,
        clinicalColumn,
        viewColumn,
        reportColumn,
      ]
    }
  }, [tab, page, pageSize])

  const context = useMemo(
    () => ({
      onPreviewReport: handlePreviewReport,
      onViewCamp: (camp: any) => {
        dispatch(setSelectedCamp(camp))
        startTransition(() => {
          navigate('/opdPatientView', { state: { camp } })
        })
      },
      onEditCamp: (camp: any) => {
        setEditingCamp(camp)
        setOpen(true)
      },
      onOpenLabReport: (camp: any) => {
        setLabReportCamp(camp)
        setLabReportFromDate('')
        setLabReportToDate('')
        setLabReportDialogOpen(true)
      },
      onNavigateToECG: (camp: any) => {
        dispatch(setSelectedCamp(camp))
        startTransition(() => {
          navigate('/ecgList', { state: { camp } })
        })
      },
      onNavigateToPFT: (camp: any) => {
        dispatch(setSelectedCamp(camp))
        startTransition(() => {
          navigate('/pftList', { state: { camp } })
        })
      },
    }),
    [dispatch, navigate]
  )

  const handleAddType = async () => {
    setLoadingType(true)
    const data = { name: newType, description: newDesc }
    const handleAddTypeResp = await createCampType(data)
    setLoadingType(false)
    setAddTypeOpen(false)
    setNewType('')
    setNewDesc('')
    toast(<ToastMessage message={handleAddTypeResp?.message} />, {
      ...toastOptions,
      type: handleAddTypeResp?.success ? 'success' : 'error',
    })
  }

  const refreshCurrentTabData = useCallback(async () => {
    const currentPage = page
    const today = new Date()
    const tomorrow = new Date(today)
    tomorrow.setDate(today.getDate() + 1)
    const yesterday = new Date(today)
    yesterday.setDate(today.getDate() - 1)

    if (tab === 0) {
      // Active camps tab
      const start = fromDate || formatDate(today)
      const end = toDate || formatDate(today)
      await handleGetActiveCamps(start, end, currentPage, pageSize)
    } else if (tab === 1) {
      // Upcoming camps tab
      const start = fromDate || formatDate(tomorrow)
      await handleGetUpcomingCamps(start, currentPage, pageSize)
    } else if (tab === 2) {
      // Completed camps tab
      const end = toDate || formatDate(yesterday)
      await handleGetCompletedCamps(end, currentPage, pageSize)
    }
  }, [
    tab,
    page,
    pageSize,
    fromDate,
    toDate,
    formatDate,
    handleGetActiveCamps,
    handleGetUpcomingCamps,
    handleGetCompletedCamps,
  ])

  const handleCampSaved = async (savedCamp?: any) => {
    handleDialogClose()
    await refreshCurrentTabData()
    if (savedCamp) {
      toast.success('Camp saved successfully')
    }
  }

  const handleOpenCreate = () => {
    setEditingCamp(null)
    setOpen(true)
  }

  const handleLabReportDownload = async () => {
    if (!labReportFromDate || !labReportToDate) {
      toast.error('Please select both From Date and To Date')
      return
    }

    if (!labReportCamp) {
      toast.error('Camp information is missing')
      return
    }

    try {
      setIsDownloadingLabReport(true)
      const response = await downloadCampLabOrders(
        labReportCamp.campId,
        labReportFromDate,
        labReportToDate
      )

      if (response.success) {
        toast.success(response.message || 'Lab report downloaded successfully')
        setLabReportDialogOpen(false)
        setLabReportFromDate('')
        setLabReportToDate('')
        setLabReportCamp(null)
      } else {
        toast.error(response.message || 'Failed to download lab report')
      }
    } catch (error: any) {
      console.error('Lab report download error:', error)
      toast.error(error.message || 'Failed to download lab report')
    } finally {
      setIsDownloadingLabReport(false)
    }
  }

  return (
    <div className={styles.campManagement}>
      <div className={styles.campHeaderRow}>
        <div className={styles.campTabs}>
          <StyledTabs value={tab} onChange={handleTabChange}>
            <Tab label={`${campTabs.active} Camps`} />
            <Tab label={`${campTabs.upComing} Camps`} />
            <Tab label={`${campTabs.completed} Camps`} />
          </StyledTabs>
        </div>
        <div className={styles.campHeaderActions}>
          <DateFilter
            value={fromDate}
            onChange={setFromDate}
            placeholder='From Date'
            iconSize={16}
          />
          <DateFilter
            value={toDate}
            onChange={setToDate}
            placeholder='To Date'
            iconSize={16}
          />
          <Button
            variant='outlined'
            color='primary'
            startIcon={<AddIcon fontSize='small' />}
            onClick={() => setAddTypeOpen(true)}
            className={styles.campCreateTypeBtn}
            size='small'
          >
            CAMP TYPE
          </Button>
          <Button
            variant='contained'
            color='primary'
            onClick={handleOpenCreate}
            startIcon={<AddIcon fontSize='small' />}
            className={styles.campCreateBtn}
            size='small'
          >
            CAMP
          </Button>
        </div>
      </div>

      <div className={styles.campTableWrapper}>
        <MedAgDataGrid
          className={`${styles.campListGrid} ag-theme-alpine`}
          tableHeight='100%'
          rowData={gridData}
          columnDefs={columnDefs}
          pagination={true}
          pageSizeOptions={[10, 25, 50]}
          page={page}
          setPage={safeSetPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          totalRows={totalElements ?? gridData.length}
          headerHeight={30}
          filterActions={null}
          globalSearch={null}
          searchField={false}
          exportIcon={false}
          context={context}
          loading={loading}
        />
      </div>

      <ReportPreviewDialog
        open={reportDialogOpen}
        reportData={reportData}
        onClose={() => setReportDialogOpen(false)}
        className='camp-report-preview-dialog'
        loading={isLoadingReport}
      />

      <Dialog
        open={openUpdateDialog}
        onClose={() => !isUploading && setOpenUpdateDialog(false)}
        maxWidth='sm'
        fullWidth
        className={styles.updateCampDialog}
      >
        <DialogTitle>{'Upload Camp Participants'}</DialogTitle>
        <DialogContent>
          <div className={classes.uploadContainer}>
            <input
              type='file'
              onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
              style={{ display: 'none' }}
              id='file-upload'
              accept='.xlsx,.xls,.csv'
            />
            <Box className={classes.uploadContainer}>
              <label htmlFor='file-upload'>
                <Button
                  variant='outlined'
                  component='span'
                  startIcon={<CloudUploadIcon />}
                >
                  Choose File
                </Button>
              </label>
              {selectedFile && (
                <Box className={classes.fileInfoContainer}>
                  <Typography variant='body2'>{selectedFile?.name}</Typography>
                  <IconButton
                    size='small'
                    onClick={(e: any) => {
                      e?.stopPropagation()
                      setSelectedFile(null)
                      // Reset file input
                      const input = document.getElementById(
                        'file-upload'
                      ) as HTMLInputElement
                      if (input) input.value = ''
                    }}
                    color='error'
                  >
                    <CloseIcon fontSize='small' />
                  </IconButton>
                </Box>
              )}
            </Box>
            <Typography
              variant='caption'
              color='textSecondary'
              className={classes.fileTypeHint}
            >
              Supports .xlsx and .xls files (Max 10MB)
            </Typography>
          </div>
        </DialogContent>
        <DialogActions className={classes.dialogActions}>
          <Button
            onClick={() => {
              setSelectedFile(null)
              setOpenUpdateDialog(false)
            }}
            disabled={isUploading}
          >
            Cancel
          </Button>
          <Button
            variant='contained'
            color='primary'
            disabled={!selectedFile || isUploading}
            onClick={handleFileUpload}
          >
            {isUploading ? 'Uploading...' : 'Upload File'}
          </Button>
        </DialogActions>
      </Dialog>
      <CreateCampComponent
        open={open}
        handleClose={handleDialogClose}
        className='camp-create-camp-component'
        initialData={editingCamp}
        isEdit={!!editingCamp}
        onSaved={handleCampSaved}
      />
      <Dialog
        open={labReportDialogOpen}
        onClose={() => !isDownloadingLabReport && setLabReportDialogOpen(false)}
        maxWidth='sm'
        fullWidth
        className={styles.labReportDialog}
      >
        <DialogTitle>
          Lab Report Export
          {labReportCamp && ` - ${labReportCamp.name}`}
        </DialogTitle>
        <DialogContent>
          <Box display='flex' flexDirection='column' gap={3} pt={2}>
            <Box>
              <Typography variant='body2' sx={{ mb: 1, fontWeight: 500 }}>
                From Date <span style={{ color: 'red' }}>*</span>
              </Typography>
              <TextField
                type='date'
                value={labReportFromDate}
                onChange={(e) => setLabReportFromDate(e.target.value)}
                fullWidth
                size='small'
                InputLabelProps={{ shrink: true }}
                disabled={isDownloadingLabReport}
              />
            </Box>
            <Box>
              <Typography variant='body2' sx={{ mb: 1, fontWeight: 500 }}>
                To Date <span style={{ color: 'red' }}>*</span>
              </Typography>
              <TextField
                type='date'
                value={labReportToDate}
                onChange={(e) => setLabReportToDate(e.target.value)}
                fullWidth
                size='small'
                InputLabelProps={{ shrink: true }}
                disabled={isDownloadingLabReport}
                inputProps={{
                  min: labReportFromDate || undefined,
                }}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions className={classes.dialogActions}>
          <Button
            onClick={() => {
              setLabReportDialogOpen(false)
              setLabReportFromDate('')
              setLabReportToDate('')
              setLabReportCamp(null)
            }}
            disabled={isDownloadingLabReport}
          >
            Cancel
          </Button>
          <Button
            variant='contained'
            color='primary'
            disabled={
              !labReportFromDate || !labReportToDate || isDownloadingLabReport
            }
            onClick={handleLabReportDownload}
          >
            {isDownloadingLabReport ? 'Downloading...' : 'Download'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={addTypeOpen}
        onClose={() => setAddTypeOpen(false)}
        maxWidth='xs'
        fullWidth
        data-testid='camp-type-dialog'
      >
        <DialogTitle>Add Type of Camp</DialogTitle>
        <DialogContent dividers>
          {loadingType ? (
            <div className={styles.appLoader}>
              <ScaleLoader color='#004C91' />
            </div>
          ) : (
            <Box component='form'>
              <Box display={'flex'} flexDirection={'column'} gap={1} mt={1}>
                <Typography>
                  Type <span className={styles.patientRegAsterisk}>*</span>
                </Typography>
                <TextField
                  value={newType}
                  onChange={(e) => setNewType(e.target.value)}
                  size='small'
                  margin='none'
                  InputProps={{
                    disableUnderline: true,
                    className: 'patient-reg-input',
                  }}
                  placeholder='Enter camp type'
                  fullWidth
                  title={newType}
                />
              </Box>

              <Box display={'flex'} flexDirection={'column'} gap={1} mt={1}>
                <Typography>Description</Typography>
                <TextField
                  value={newDesc}
                  onChange={(e) => setNewDesc(e.target.value)}
                  multiline
                  rows={3}
                  size='small'
                  margin='none'
                  InputProps={{
                    disableUnderline: true,
                  }}
                  placeholder='Description'
                  fullWidth
                  title={newDesc}
                />
              </Box>
            </Box>
          )}
        </DialogContent>
        {!loadingType && (
          <DialogActions>
            <Button
              onClick={() => setAddTypeOpen(false)}
              color='secondary'
              className={styles.campCreateTypeDialogCancel}
              size='small'
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddType}
              variant='contained'
              color='primary'
              disabled={!newType}
              className={styles.campCreateTypeDialogAdd}
              size='small'
            >
              Add
            </Button>
          </DialogActions>
        )}
      </Dialog>
    </div>
  )
}

export default ListOfCamps
